import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CollectionResponse, type ResourceResponse } from '@shared/api/client';
import type { Cliente, ClienteFormData } from '../types/cliente';

const BASE = '/billing/clients';

export const clienteKeys = {
  all: ['clientes'] as const,
  list: (emisorId?: number, q?: string) => [...clienteKeys.all, 'list', emisorId ?? 0, q ?? ''] as const,
  detail: (id: number) => [...clienteKeys.all, 'detail', id] as const,
};

export function useClientes(emisorId?: number, q?: string) {
  return useQuery({
    queryKey: clienteKeys.list(emisorId, q),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Cliente>>(BASE, {
        params: { emisor_id: emisorId, q },
      });
      return data.data;
    },
  });
}

export function useCliente(id: number) {
  return useQuery({
    queryKey: clienteKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Cliente>>(`${BASE}/${id}`);
      return data.data;
    },
    enabled: id > 0,
  });
}

export function useCreateCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ClienteFormData) => {
      const { data } = await api.post<ResourceResponse<Cliente>>(BASE, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: clienteKeys.all }),
  });
}

export function useUpdateCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<ClienteFormData> }) => {
      const { data } = await api.put<ResourceResponse<Cliente>>(`${BASE}/${id}`, payload);
      return data.data;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: clienteKeys.all });
      qc.invalidateQueries({ queryKey: clienteKeys.detail(v.id) });
    },
  });
}

export function useDeleteCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: clienteKeys.all }),
  });
}
